/* =========================================================
   Ankit START - DFW BXGY Engine
   File: assets/dfw-bxgy-engine.js

   Purpose:
   - Global BXGY registry + evaluator
   - Reads .dfw-bxgy-config JSON blocks rendered by Liquid
   - Tracks paid cart quantities
   - Evaluates active tier / next tier / free earned
   - Dispatches one global event: dfw:bxgy-updated
   - Supports lazy-loaded and AJAX-inserted content
   - Persists registry + titles in sessionStorage so deals
     survive page navigation without losing configs

   Core Rules:
   - Free items are NEVER added to cart
   - BXGY buttons NEVER disable
   - BXGY buttons always add buy_qty
   - Liquid provides deal data; JS evaluates truth
   - Cart is always source of truth for quantities
   ========================================================= */

(function () {
  'use strict';

  if (window.DFWBXGY) return;

  var REGISTRY = {};
  var CART_MAP = {};
  var STATE = {};
  var OBSERVER = null;
  var SCAN_TIMER = null;
  var INITIALIZED = false;

  var CONFIG_SELECTOR  = '.dfw-bxgy-entry';
  var UPDATE_EVENT     = 'dfw:bxgy-updated';
  var SESSION_KEY      = 'dfw_bxgy_registry';   // sessionStorage key

  /* =========================================================
     Utilities
     ========================================================= */

  function toNumber(value, fallback) {
    var n = Number(value);
    return Number.isFinite(n) ? n : (fallback || 0);
  }

  function toProductId(value) {
    if (value === null || value === undefined || value === '') return '';
    return String(value);
  }

  function safeParseJSON(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  function debounceScan(fn, wait) {
    clearTimeout(SCAN_TIMER);
    SCAN_TIMER = setTimeout(fn, wait || 120);
  }

  function shallowClone(obj) {
    return Object.assign({}, obj);
  }

  function sortDeals(deals) {
    return deals.slice().sort(function (a, b) {
      var buyDiff = toNumber(a.buy_qty) - toNumber(b.buy_qty);
      if (buyDiff !== 0) return buyDiff;
      return toNumber(a.sort_order) - toNumber(b.sort_order);
    });
  }

  function moneyFromCents(cents) {
    return toNumber(cents, 0) / 100;
  }

  function hasRelevantBxgyNode(node) {
    if (!node || node.nodeType !== 1) return false;
    if (typeof node.matches === 'function' && node.matches(CONFIG_SELECTOR)) return true;
    if (typeof node.querySelector === 'function' && node.querySelector(CONFIG_SELECTOR)) return true;
    return false;
  }

  /* =========================================================
     sessionStorage — persist registry across page loads
     ========================================================= */

  function saveRegistry() {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(REGISTRY));
    } catch (e) {}
  }

  function loadRegistry() {
    try {
      var raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return;

      var saved = JSON.parse(raw);

      Object.keys(saved).forEach(function (pid) {
        /* only restore if not already registered from current page */
        if (!REGISTRY[pid]) {
          REGISTRY[pid] = saved[pid];
        }
      });

    } catch (e) {}
  }

  /* =========================================================
     Config / Registry
     ========================================================= */

  function normalizeDeal(raw) {
    if (!raw) return null;

    var buyQty    = toNumber(raw.buy_qty, 0);
    var freeQty   = toNumber(raw.free_qty, 0);
    var sortOrder = toNumber(raw.sort_order, 0);

    if (buyQty <= 0 || freeQty <= 0) return null;

    return {
      buy_qty:    buyQty,
      free_qty:   freeQty,
      sort_order: sortOrder
    };
  }

  function normalizeConfig(raw) {
    if (!raw) return null;

    var productId = toProductId(raw.product_id);
    if (!productId) return null;

    var deals = Array.isArray(raw.deals) ? raw.deals : [];
    deals = deals.map(normalizeDeal).filter(Boolean);

    return {
      product_id: productId,
      title:      raw.title || '',    // ← persisted title
      deals:      sortDeals(deals)
    };
  }

  function registerConfig(rawConfig) {
    var config = normalizeConfig(rawConfig);
    if (!config || !config.product_id) return;

    /* merge — don't overwrite a title we already have */
    var existing = REGISTRY[config.product_id];
    if (existing && !config.title) {
      config.title = existing.title;
    }

    REGISTRY[config.product_id] = config;

    /* persist immediately after every registration */
    saveRegistry();
  }

  function registerConfigs(configs) {
    if (!Array.isArray(configs)) return;
    configs.forEach(registerConfig);
  }

  /* =========================================================
     Title helper — reads from DOM row, stores in registry
     ========================================================= */

  function extractTitleFromRow(row, productId) {

    if (!row) return '';

    /* Source 1 — data-json-product */
    if (row.dataset.jsonProduct) {
      try {
        var parsed = JSON.parse(row.dataset.jsonProduct);
        var t = parsed.name || parsed.title;
        if (t) return t;
      } catch (e) {}
    }

    /* Source 2 — data-product-title */
    if (row.dataset.productTitle) return row.dataset.productTitle;

    /* Source 3 — img alt */
    var img = row.querySelector('img[alt]');
    if (img && img.alt) return img.alt;

    return '';
  }

  /* =========================================================
     Scan page configs
     ========================================================= */

  function scanPageConfigs(root) {

    var scope = root && root.querySelectorAll ? root : document;

    /* 1 — Script configs (.dfw-bxgy-entry) — product page */
    scope.querySelectorAll('.dfw-bxgy-entry').forEach(function (node) {

      if (node.dataset.dfwRegistered === 'true') return;

      var parsed = safeParseJSON(node.textContent || '');
      if (!parsed) return;

      registerConfig(parsed);
      node.dataset.dfwRegistered = 'true';
    });

    /* 2 — DOM attributes ([data-bxgy]) — Express, cards, quickview */
    scope.querySelectorAll('[data-bxgy]').forEach(function (row) {

      if (row.dataset.bxgyRegistered === 'true') return;

      var bxgy = row.dataset.bxgy;
      if (!bxgy || bxgy === 'null') return;

      var id = row.dataset.productId;
      if (!id) return;

      id = id.replace('product-', '');

      try {

        var raw = JSON.parse(bxgy);
        if (!Array.isArray(raw)) return;

        var deals = raw.map(function (d) {
          return {
            buy_qty:  d.buy_qty  || d.buy_quantity,
            free_qty: d.free_qty || d.get_quantity
          };
        });

        /* extract title from this row and pass into registry */
        var title = extractTitleFromRow(row, id);

        registerConfig({
          product_id: id,
          deals:      deals,
          title:      title
        });

        row.dataset.bxgyRegistered = 'true';

      } catch (err) {
        console.warn('BXGY data parse error', id, err);
      }

    });
  }

  /* =========================================================
     Cart Map
     ========================================================= */

  function emptyCartMap() {
    CART_MAP = {};
  }

  function ensureCartEntry(productId) {
    productId = toProductId(productId);
    if (!productId) return null;

    if (!CART_MAP[productId]) {
      CART_MAP[productId] = {
        product_id: productId,
        variant_id: '',
        quantity:   0,
        unit_price: 0,
        line_keys:  []
      };
    }

    return CART_MAP[productId];
  }

  function setCartFromPayload(cart) {
    emptyCartMap();

    if (!cart || !Array.isArray(cart.items)) return;

    cart.items.forEach(function (item) {

      var productId = toProductId(item.product_id || (item.product && item.product.id));
      if (!productId) return;

      var qty = toNumber(item.quantity, 0);
      var unitPrice = toNumber(
        item.final_price       != null ? item.final_price :
        item.price             != null ? item.price :
        item.final_line_price  != null && qty > 0 ? (item.final_line_price / qty) :
        0,
        0
      );

      var entry = ensureCartEntry(productId);
      if (!entry) return;

      entry.quantity += qty;

      if (!entry.variant_id && item.variant_id) {
        entry.variant_id = String(item.variant_id);
      }

      if (item.key) {
        entry.line_keys.push(String(item.key));
      }

      if (!entry.unit_price && unitPrice) {
        entry.unit_price = unitPrice;
      }

      /* ← if product is in cart, try to grab title from cart item
         and backfill into registry if we don't have it yet */
      if (REGISTRY[productId] && !REGISTRY[productId].title && item.title) {
        REGISTRY[productId].title = item.title.split(' - ')[0]; // strip variant suffix
        saveRegistry();
      }

    });
  }

  function addCartEntry(productId, qty, unitPrice) {
    var entry = ensureCartEntry(productId);
    if (!entry) return;

    entry.quantity += toNumber(qty, 0);

    if (unitPrice != null) {
      entry.unit_price = toNumber(unitPrice, 0);
    }
  }

  function setCartEntry(productId, qty, unitPrice) {
    var entry = ensureCartEntry(productId);
    if (!entry) return;

    entry.quantity = toNumber(qty, 0);

    if (unitPrice != null) {
      entry.unit_price = toNumber(unitPrice, 0);
    }
  }

  function getCartQty(productId) {
    productId = toProductId(productId);
    if (!productId || !CART_MAP[productId]) return 0;
    return toNumber(CART_MAP[productId].quantity, 0);
  }

  function getUnitPrice(productId) {
    productId = toProductId(productId);
    if (!productId || !CART_MAP[productId]) return 0;
    return toNumber(CART_MAP[productId].unit_price, 0);
  }

  function getVariantId(productId) {
    productId = toProductId(productId);
    if (!productId || !CART_MAP[productId]) return '';
    return CART_MAP[productId].variant_id || '';
  }

  async function fetchCartJSON() {
    var response = await fetch('/cart.js', {
      method:      'GET',
      headers:     { 'Accept': 'application/json' },
      credentials: 'same-origin'
    });

    if (!response.ok) throw new Error('Unable to fetch /cart.js');

    return await response.json();
  }

  async function refreshFromCart(cartPayload) {
    if (cartPayload && cartPayload.items) {
      setCartFromPayload(cartPayload);
      evaluateAll();
      return;
    }

    try {
      var cart = await fetchCartJSON();
      setCartFromPayload(cart);
      evaluateAll();
    } catch (error) {}
  }

  /* =========================================================
     Evaluation
     ========================================================= */

  function buildTierStates(deals, cartQty, activeTier) {
    return deals.map(function (tier) {
      var earned = cartQty >= tier.buy_qty;
      var active = !!activeTier &&
        tier.buy_qty  === activeTier.buy_qty &&
        tier.free_qty === activeTier.free_qty;

      return {
        buy_qty:    tier.buy_qty,
        free_qty:   tier.free_qty,
        sort_order: tier.sort_order,
        earned:     earned,
        active:     active,
        remaining:  Math.max(0, tier.buy_qty - cartQty)
      };
    });
  }

  function findActiveTier(deals, cartQty) {
    var activeTier = null;
    deals.forEach(function (tier) {
      if (cartQty >= tier.buy_qty) activeTier = tier;
    });
    return activeTier;
  }

  function findNextTier(deals, cartQty) {
    for (var i = 0; i < deals.length; i += 1) {
      if (cartQty < deals[i].buy_qty) return deals[i];
    }
    return null;
  }

  function buildMessages(productState) {
    var rewardMessage   = '';
    var progressMessage = '';
    var freeEarned      = toNumber(productState.free_qty_earned, 0);
    var nextTier        = productState.next_tier;
    var remaining       = toNumber(productState.remaining_to_next, 0);

    if (freeEarned > 0) {
      rewardMessage = 'You earned ' + freeEarned + ' FREE';
    }

    if (nextTier && remaining > 0) {
      progressMessage = 'Add ' + remaining + ' more to earn ' + nextTier.free_qty + ' FREE';
    }

    return { reward: rewardMessage, progress: progressMessage };
  }

  function evaluateProduct(productId) {
    productId = toProductId(productId);
    var entry = REGISTRY[productId];

    if (!entry) return null;

    var deals     = Array.isArray(entry.deals) ? entry.deals : [];
    var cartQty   = getCartQty(productId);
    var unitPrice = getUnitPrice(productId);
    var variantId = getVariantId(productId);
    var title     = entry.title || '';

    if (!deals.length) {
      return {
        product_id:              productId,
        variant_id:              variantId,
        title:                   title,
        has_deals:               false,
        cart_qty:                cartQty,
        unit_price:              unitPrice,
        active_tier:             null,
        next_tier:               null,
        deal_multiplier:         0,
        free_qty_earned:         0,
        free_value_earned_cents: 0,
        remaining_to_next:       null,
        tier_states:             [],
        messages:                { reward: '', progress: '' }
      };
    }

    var activeTier           = findActiveTier(deals, cartQty);
    var nextTier             = findNextTier(deals, cartQty);
    var multiplier           = 0;
    var freeQtyEarned        = 0;
    var freeValueEarnedCents = 0;

    if (activeTier) {
      multiplier            = Math.floor(cartQty / activeTier.buy_qty);
      freeQtyEarned         = multiplier * activeTier.free_qty;
      freeValueEarnedCents  = freeQtyEarned * unitPrice;
    }

    var productState = {
      product_id:              productId,
      variant_id:              variantId,
      title:                   title,        // ← surfaced in state
      has_deals:               true,
      cart_qty:                cartQty,
      unit_price:              unitPrice,
      active_tier:             activeTier ? shallowClone(activeTier) : null,
      next_tier:               nextTier   ? shallowClone(nextTier)   : null,
      deal_multiplier:         multiplier,
      free_qty_earned:         freeQtyEarned,
      free_value_earned_cents: freeValueEarnedCents,
      remaining_to_next:       nextTier ? Math.max(0, nextTier.buy_qty - cartQty) : null,
      tier_states:             buildTierStates(deals, cartQty, activeTier),
      messages:                { reward: '', progress: '' }
    };

    productState.messages = buildMessages(productState);

    return productState;
  }

  function evaluateAll() {
    var nextState = {};

    Object.keys(REGISTRY).forEach(function (productId) {
      nextState[productId] = evaluateProduct(productId);
    });

    STATE = nextState;
    dispatchUpdate();
  }

  /* =========================================================
     Footer Candidate Logic
     ========================================================= */

  function getAllProductStates() {
    return Object.keys(STATE).map(function (key) { return STATE[key]; }).filter(Boolean);
  }

  function getFooterDeal() {
    var productStates = getAllProductStates();
    if (!productStates.length) return null;

    var progressCandidates = productStates
      .filter(function (item) {
        return item && item.next_tier && toNumber(item.remaining_to_next, 0) > 0;
      })
      .sort(function (a, b) {
        return toNumber(a.remaining_to_next, 999999) - toNumber(b.remaining_to_next, 999999);
      });

    if (progressCandidates.length) {
      return {
        product_id: progressCandidates[0].product_id,
        mode:       'progress',
        message:    progressCandidates[0].messages.progress,
        state:      progressCandidates[0]
      };
    }

    var rewardCandidates = productStates
      .filter(function (item) {
        return item && toNumber(item.free_qty_earned, 0) > 0;
      })
      .sort(function (a, b) {
        return toNumber(b.free_qty_earned, 0) - toNumber(a.free_qty_earned, 0);
      });

    if (rewardCandidates.length) {
      return {
        product_id: rewardCandidates[0].product_id,
        mode:       'reward',
        message:    rewardCandidates[0].messages.reward,
        state:      rewardCandidates[0]
      };
    }

    return null;
  }

  /* =========================================================
     Helpers for UI Surfaces
     ========================================================= */

  function getState()        { return STATE; }
  function getRegistry()     { return REGISTRY; }
  function getCartMap()      { return CART_MAP; }

  function getProductState(productId) {
    productId = toProductId(productId);
    return STATE[productId] || null;
  }

  function getTierStates(productId) {
    var s = getProductState(productId);
    return s ? s.tier_states : [];
  }

  function getRemaining(productId) {
    var s = getProductState(productId);
    return s ? s.remaining_to_next : null;
  }

  function getNextTier(productId) {
    var s = getProductState(productId);
    return s ? s.next_tier : null;
  }

  function getFreeEarned(productId) {
    var s = getProductState(productId);
    return s ? s.free_qty_earned : 0;
  }

  function getFreeValueEarned(productId) {
    var s = getProductState(productId);
    return s ? moneyFromCents(s.free_value_earned_cents) : 0;
  }

  function getDealAddQty(productId, dealBuyQty) {
    productId = toProductId(productId);
    if (!REGISTRY[productId]) return 0;
    return Math.max(0, toNumber(dealBuyQty, 0));
  }

  /* =========================================================
     Event System
     ========================================================= */

  function dispatchUpdate() {
    document.dispatchEvent(new CustomEvent(UPDATE_EVENT, {
      detail: {
        registry:   REGISTRY,
        cartMap:    CART_MAP,
        state:      STATE,
        footerDeal: getFooterDeal()
      }
    }));
  }

  /* =========================================================
     DOM Watching / Lazy Load Support
     ========================================================= */

  function handlePotentialDOMChange() {
    debounceScan(function () {
      scanPageConfigs();
      evaluateAll();
    }, 120);
  }

  function bindMutationObserver() {
    if (OBSERVER) return;

    OBSERVER = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes || [], function (node) {
          return hasRelevantBxgyNode(node);
        });
      });

      if (relevant) handlePotentialDOMChange();
    });

    OBSERVER.observe(document.body, { childList: true, subtree: true });
  }

  /* =========================================================
     Cart Event Integration
     ========================================================= */

  function extractCart(event) {
    if (!event || !event.detail) return null;
    if (event.detail.items) return event.detail;
    if (event.detail.cart && event.detail.cart.items) return event.detail.cart;
    return null;
  }

  function bindCartEvents() {

    document.addEventListener('cart:refresh', function (event) {
      refreshFromCart(extractCart(event));
    });

    document.addEventListener('dfw:cart-updated', function (event) {
      refreshFromCart(extractCart(event));
    });

    document.addEventListener('dfw:cart-bus-updated', function (event) {
      refreshFromCart(extractCart(event));
    });
  }

  /* =========================================================
     Public Init
     ========================================================= */

  async function init() {
    if (INITIALIZED) return;
    INITIALIZED = true;

    loadRegistry();          // ← restore saved registry first
    scanPageConfigs();       // ← then add anything new on this page
    bindCartEvents();
    bindMutationObserver();
    await refreshFromCart(); // ← cart quantities + title backfill
  }

  /* =========================================================
     Public API
     ========================================================= */

  window.DFWBXGY = {
    init:            init,
    scanPageConfigs: scanPageConfigs,
    registerConfig:  registerConfig,
    registerConfigs: registerConfigs,
    refreshFromCart: refreshFromCart,

    setCartFromPayload: function (cart) {
      setCartFromPayload(cart);
      evaluateAll();
    },

    addCartEntry: function (productId, qty, unitPrice) {
      addCartEntry(productId, qty, unitPrice);
      evaluateAll();
    },

    setCartEntry: function (productId, qty, unitPrice) {
      setCartEntry(productId, qty, unitPrice);
      evaluateAll();
    },

    evaluateAll:     evaluateAll,
    evaluateProduct: evaluateProduct,

    getState:           getState,
    getRegistry:        getRegistry,
    getCartMap:         getCartMap,
    getProductState:    getProductState,
    getTierStates:      getTierStates,
    getRemaining:       getRemaining,
    getNextTier:        getNextTier,
    getFreeEarned:      getFreeEarned,
    getFreeValueEarned: getFreeValueEarned,
    getDealAddQty:      getDealAddQty,
    getFooterDeal:      getFooterDeal
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* =========================================================
   Ankit START - BXGY Express Lazy Load Registration
   ========================================================= */

document.addEventListener('dfw:products-rendered', function () {

  if (!window.DFWBXGY) return;

  document.querySelectorAll('.dfw-export-item').forEach(function (row) {

    if (row.dataset.bxgyRegistered) return;
    row.dataset.bxgyRegistered = '1';

    var bxgy = row.dataset.bxgy;
    if (!bxgy || bxgy === 'null') return;

    var id = row.dataset.productId;
    if (!id) return;

    id = id.replace('product-', '');

    try {

      var raw = JSON.parse(bxgy);
      if (!Array.isArray(raw) || !raw.length) return;

      var deals = raw.map(function (d) {
        return {
          buy_qty:  d.buy_qty  || d.buy_quantity,
          free_qty: d.free_qty || d.get_quantity
        };
      });

      window.DFWBXGY.registerConfig({
        product_id: id,
        deals:      deals
      });

    } catch (err) {
      console.warn('BXGY Express parse error', id, err);
    }

  });

  window.DFWBXGY.evaluateAll();

});

/* =========================================================
   Ankit END - BXGY Express Lazy Load Registration
   ========================================================= */

/* =========================================================
   Ankit START - BXGY Auto Detection (Quickview / AJAX)
   ========================================================= */

(function () {

  if (!window.DFWBXGY) return;

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (
          (node.matches && node.matches('[data-bxgy]')) ||
          (node.querySelector && node.querySelector('[data-bxgy]'))
        ) {
          window.DFWBXGY.scanPageConfigs();
          window.DFWBXGY.evaluateAll();
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

})();

/* =========================================================
   Ankit END - BXGY Auto Detection
   ========================================================= */